import { authenticatedFetch } from "./authenticatedFetch";
import { formatApiErrorMessage } from "../utils/apiErrorMessage";
import {
  isHtmlApiResponse,
  parseApiResponseBody,
} from "../utils/parseApiResponseBody";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const SIGNATURE_API_ERROR_PT = {
  "Document not found": "Documento não encontrado",
};

const LICENSE_ERROR_PT = {
  LICENSE_SIGNATURE_LIMIT_EXCEEDED:
    "Você não possui assinaturas disponíveis na sua licença. Adquira um plano com assinaturas ou contate nosso suporte.",
  LICENSE_NOT_FOUND: "Licença não encontrada.",
  LICENSE_EXPIRED: "Sua licença está expirada ou inativa.",
  LICENSE_MEMBERSHIP_REVOKED: "Você não faz mais parte desta licença.",
  LICENSE_MEMBERSHIP_PENDING: "Aceite o convite da licença antes de enviar para assinatura.",
  LICENSE_NOT_VALID: "Sua licença não permite enviar documentos para assinatura.",
};

/**
 * @param {string | undefined} raw
 * @param {number} [httpStatus]
 * @param {string | undefined} [code]
 * @returns {string}
 */
const signatureErrorMessagePt = (raw, httpStatus, code) => {
  if (code && LICENSE_ERROR_PT[code]) {
    return LICENSE_ERROR_PT[code];
  }

  if (raw != null && typeof raw !== "string") {
    const formatted = formatApiErrorMessage(raw, "");
    if (formatted) return formatted;
  }
  if (raw && typeof raw === "string") {
    const t = raw.trim();
    if (SIGNATURE_API_ERROR_PT[t]) return SIGNATURE_API_ERROR_PT[t];
    if (t) return t;
  }
  if (httpStatus === 403) {
    return LICENSE_ERROR_PT.LICENSE_NOT_VALID;
  }
  if (httpStatus === 404) return "Documento não encontrado";
  return "Não foi possível concluir a operação de assinatura.";
};

async function readSignatureApiBody(response) {
  const data = await parseApiResponseBody(response);
  if (isHtmlApiResponse(response, data)) {
    return {
      data,
      messagePt:
        data?.message ||
        "Não foi possível contactar a API de assinatura. Verifique a configuração do ambiente.",
    };
  }
  return { data, messagePt: null };
}

function extractSignatureError(data, response) {
  const code =
    (typeof data?.code === "string" && data.code.trim()) || undefined;
  const raw =
    (typeof data?.message === "string" && data.message.trim()
      ? data.message.trim()
      : null) ||
    data?.error ||
    (data?.status === "ERROR" ? data?.error : null) ||
    null;

  return {
    code,
    messagePt: signatureErrorMessagePt(raw, response.status, code),
  };
}

/**
 * @returns {Promise<{ ok: true } | { ok: false, messagePt: string, code?: string }>}
 */
export const validateSignatureJob = async (jobId) => {
  if (!jobId) {
    return { ok: false, messagePt: "Documento inválido." };
  }

  const response = await authenticatedFetch(
    `${API_BASE_URL}/signatures/${encodeURIComponent(jobId)}/validate`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    }
  );
  const { data, messagePt: htmlMessage } = await readSignatureApiBody(response);
  if (htmlMessage) {
    return { ok: false, messagePt: htmlMessage };
  }

  if (response.status === 200 && data?.status === "VALID") {
    return { ok: true };
  }

  const { code, messagePt } = extractSignatureError(data, response);
  return { ok: false, messagePt, code };
};

/**
 * @returns {Promise<{ ok: true } | { ok: false, messagePt: string, code?: string }>}
 */
export const signDocumentForJob = async (jobId, notificationChannel = 'Email') => {
  if (!jobId) {
    return { ok: false, messagePt: "Documento inválido." };
  }

  const response = await authenticatedFetch(
    `${API_BASE_URL}/signatures/${encodeURIComponent(jobId)}/sign-document`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ notification_channel: notificationChannel || 'Email' }),
    }
  );
  const { data, messagePt: htmlMessage } = await readSignatureApiBody(response);
  if (htmlMessage) {
    return { ok: false, messagePt: htmlMessage };
  }

  if (response.ok) {
    return { ok: true };
  }

  const { code, messagePt } = extractSignatureError(data, response);
  return { ok: false, messagePt, code };
};

/**
 * POST /signatures/:job_id/cancel — body: `{ status: 200, message: "" }` on success.
 * @returns {Promise<{ ok: true } | { ok: false, messagePt: string }>}
 */
export const cancelSignatureJob = async (jobId) => {
  if (!jobId) {
    return { ok: false, messagePt: "Documento inválido." };
  }

  const response = await authenticatedFetch(
    `${API_BASE_URL}/signatures/${encodeURIComponent(jobId)}/cancel`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    }
  );
  const { data, messagePt: htmlMessage } = await readSignatureApiBody(response);
  if (htmlMessage) {
    return { ok: false, messagePt: htmlMessage };
  }

  if (data?.status == null ? false : Number(data.status) === 200) {
    return { ok: true };
  }

  const raw =
    typeof data?.message === "string" ? data.message.trim() : "";

  return {
    ok: false,
    messagePt:
      raw ||
      extractSignatureError(data, response).messagePt,
  };
};

/**
 * GET /signatures/:job_id/status
 * @returns {Promise<{ ok: true, data: object } | { ok: false, messagePt: string }>}
 */
export const fetchSignatureStatus = async (jobId) => {
  if (!jobId) {
    return { ok: false, messagePt: "Documento inválido." };
  }

  const response = await authenticatedFetch(
    `${API_BASE_URL}/signatures/${encodeURIComponent(jobId)}/status`,
    { method: "GET" }
  );
  const { data, messagePt: htmlMessage } = await readSignatureApiBody(response);
  if (htmlMessage) {
    return { ok: false, messagePt: htmlMessage };
  }

  if (response.ok) {
    return { ok: true, data };
  }

  return {
    ok: false,
    messagePt:
      (typeof data?.error === "string" && data.error) ||
      extractSignatureError(data, response).messagePt,
  };
};

/**
 * POST /signatures/:job_id/resend — omit signer_id to resend all pending.
 * @param {string} jobId
 * @param {string} [signerId] client signer_id
 * @returns {Promise<{ ok: true, data?: object } | { ok: false, messagePt: string }>}
 */
export const resendSignatureNotification = async (jobId, signerId) => {
  if (!jobId) {
    return { ok: false, messagePt: "Documento inválido." };
  }

  const body = {};
  if (signerId) body.signer_id = signerId;

  const response = await authenticatedFetch(
    `${API_BASE_URL}/signatures/${encodeURIComponent(jobId)}/resend`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  const { data, messagePt: htmlMessage } = await readSignatureApiBody(response);
  if (htmlMessage) {
    return { ok: false, messagePt: htmlMessage };
  }

  if (response.ok) {
    return { ok: true, data };
  }

  return {
    ok: false,
    messagePt:
      (typeof data?.error === "string" && data.error) ||
      extractSignatureError(data, response).messagePt,
  };
};

/**
 * POST /signatures/:job_id/renew-expiration — extends Assinafy assignment deadline.
 * @param {string} jobId
 * @param {number} [days=14]
 * @returns {Promise<{ ok: true, data?: object } | { ok: false, messagePt: string }>}
 */
export const renewSignatureExpiration = async (jobId, days = 14) => {
  if (!jobId) {
    return { ok: false, messagePt: "Documento inválido." };
  }

  const response = await authenticatedFetch(
    `${API_BASE_URL}/signatures/${encodeURIComponent(jobId)}/renew-expiration`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ days }),
    }
  );
  const { data, messagePt: htmlMessage } = await readSignatureApiBody(response);
  if (htmlMessage) {
    return { ok: false, messagePt: htmlMessage };
  }

  if (response.ok) {
    return { ok: true, data };
  }

  return {
    ok: false,
    messagePt:
      (typeof data?.error === "string" && data.error) ||
      extractSignatureError(data, response).messagePt,
  };
};

/**
 * Uploaded PDFs for signature (not TipTap manual editor).
 */
export function isManualSignatureUpload(doc) {
  if (!doc || typeof doc !== "object") return false;
  return doc.is_manual_signature === true || doc.isManualSignature === true;
}

/**
 * Whether the document was created via the manual write-contract flow.
 * Used only to pick the correct sign endpoint — not for save/load of signature fields.
 */
export function isManualDocument(doc) {
  if (!doc || typeof doc !== "object") return false;
  const v = doc.is_manual ?? doc.isManual ?? doc.filling_type ?? doc.fillingType;
  if (v === true || v === 1) return true;
  if (typeof v === "string") {
    const t = v.trim().toUpperCase();
    return t === "TRUE" || t === "1" || t === "YES" || t === "MANUAL";
  }
  return false;
}

/**
 * Final send step only — sign endpoints still differ by document type.
 *
 * @param {string} jobId
 * @param {object} document — metadata with is_manual / filling_type
 * @param {string} [notificationChannel]
 * @returns {Promise<{ ok: true } | { ok: false, messagePt: string }>}
 */
export async function sendDocumentForSignature(
  jobId,
  document,
  notificationChannel = 'Email'
) {
  if (!jobId) {
    return { ok: false, messagePt: "Documento inválido." };
  }
  if (isManualDocument(document) || isManualSignatureUpload(document)) {
    return signManualDocumentForJob(jobId, notificationChannel);
  }
  return signDocumentForJob(jobId, notificationChannel);
}

/**
 * @returns {Promise<{ ok: true } | { ok: false, messagePt: string, code?: string }>}
 */
export const signManualDocumentForJob = async (
  jobId,
  notificationChannel = 'Email'
) => {
  if (!jobId) {
    return { ok: false, messagePt: "Documento inválido." };
  }

  const response = await authenticatedFetch(
    `${API_BASE_URL}/documents/manual/${encodeURIComponent(jobId)}/sign`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ notification_channel: notificationChannel || 'Email' }),
    }
  );
  const { data, messagePt: htmlMessage } = await readSignatureApiBody(response);
  if (htmlMessage) {
    return { ok: false, messagePt: htmlMessage };
  }

  if (response.ok) {
    return { ok: true };
  }

  const { code, messagePt } = extractSignatureError(data, response);
  return { ok: false, messagePt, code };
};
